import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { VorgangFacade } from '@schir-int-client/vorgang-shared';
import { saveAs } from 'file-saver';
import { vorgangSelector } from 'libs/vorgang-shared/src/lib/vorgang.selectors';
import { catchError, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import {
	createKorrespondenzVerfuegungByVorgangAction,
	createKorrespondenzVerfuegungByVorgangSuccessAction,
	createVerfuegungByVorgangAction,
	createVerfuegungByVorgangSuccessAction,
	createVerfuegungDruckAction,
	createVerfuegungDruckSuccessAction,
	deleteVerfuegungAction,
	deleteVerfuegungFailAction,
	deleteVerfuegungSuccessAction,
	loadVerfuegungAction,
	loadVerfuegungenByVorgangAction,
	loadVerfuegungenByVorgangSuccessAction,
	loadVerfuegungSuccessAction,
	markVerfuegungAsErledigtAction,
	markVerfuegungAsErledigtSuccessAction,
	updateVerfuegungNotizAction,
	updateVerfuegungNotizSuccessAction,
} from './verfuegung.actions';
import { VerfuegungFacade } from './verfuegung.facade';
import { VerfuegungService } from './verfuegung.service';
import { of } from 'rxjs';
import { AppNotificationService } from '@schir-int-client/tech';
import { AppState } from '../../../../apps/int-client/src/app/shared/store/app-state.model';
import { VerfahrenFacade } from '@schir-int-client/verfahren-shared';

@Injectable()
export class VerfuegungEffects {
	constructor(
		private store: Store<AppState>,
		private actions: Actions,
		private service: VerfuegungService,
		private vorgangFacade: VorgangFacade,
		private verfahrenFacade: VerfahrenFacade,
		private facade: VerfuegungFacade,
		private notificationService: AppNotificationService
	) {}

	loadVerfuegungenByVorgang = createEffect(() =>
		this.actions.pipe(
			ofType(loadVerfuegungenByVorgangAction, markVerfuegungAsErledigtSuccessAction),
			withLatestFrom(this.vorgangFacade.vorgang),
			mergeMap(([, vorgang]) =>
				this.service
					.getVerfuegungenByVorgang(vorgang)
					.pipe(map(verfuegungen => loadVerfuegungenByVorgangSuccessAction({ vorgang, verfuegungen })))
			)
		)
	);

	createVerfuegung = createEffect(() =>
		this.actions.pipe(
			ofType(createVerfuegungByVorgangAction),
			withLatestFrom(this.store.select(vorgangSelector)),
			mergeMap(([{ notiz }, vorgang]) =>
				this.service
					.createVerfuegung(notiz, vorgang.resource)
					.pipe(
						map(verfuegung =>
							createVerfuegungByVorgangSuccessAction({ vorgang: vorgang.resource, verfuegung })
						)
					)
			)
		)
	);

	deleteVerfuegung = createEffect(() =>
		this.actions.pipe(
			ofType(deleteVerfuegungAction),
			switchMap(({ verfuegung }) =>
				this.service.deleteVerfuegung(verfuegung).pipe(
					map(verfuegungen => deleteVerfuegungSuccessAction({ verfuegungen, deleted: verfuegung })),
					catchError(error => of(deleteVerfuegungFailAction({ error })))
				)
			)
		)
	);

	/**
	 * Aktuell wird der Delete-Button für alle Nicht-Administratoren ausgeblendet.
	 * Backend wirft Exception für den Fall eines unberechtigten Api-Aufrufs.
	 **/
	deleteVerfuegungFail = createEffect(
		() =>
			this.actions.pipe(
				ofType(deleteVerfuegungFailAction),
				tap(({ error }) =>
					this.notificationService.handleInfo(error.error.issues[0]['message'], null, 'Fehlende Berechtigung')
				)
			),
		{ dispatch: false }
	);

	createKorrespondenzVerfuegung = createEffect(() =>
		this.actions.pipe(
			ofType(createKorrespondenzVerfuegungByVorgangAction),
			withLatestFrom(this.store.select(vorgangSelector)),
			mergeMap(([{ notiz }, vorgang]) =>
				this.service.createKorrespondenzVerfuegung(notiz, vorgang.resource).pipe(
					map(verfuegung =>
						createKorrespondenzVerfuegungByVorgangSuccessAction({
							vorgang: vorgang.resource,
							verfuegung,
						})
					)
				)
			)
		)
	);

	loadVerfuegung = createEffect(() =>
		this.actions.pipe(
			ofType(loadVerfuegungAction),
			switchMap(({ verfuegungUri }) =>
				this.service
					.getOne(verfuegungUri)
					.pipe(
						map(verfuegung => loadVerfuegungSuccessAction({ verfuegung: { ...verfuegung, aufgaben: [] } }))
					)
			)
		)
	);

	markAsErledigt = createEffect(() =>
		this.actions.pipe(
			ofType(markVerfuegungAsErledigtAction),
			switchMap(({ verfuegung }) =>
				this.service.markAsErledigt(verfuegung).pipe(map(() => loadVerfuegungenByVorgangAction()))
			)
		)
	);

	updateNotiz = createEffect(() =>
		this.actions.pipe(
			ofType(updateVerfuegungNotizAction),
			switchMap(({ notiz, verfuegungUri }) =>
				this.service
					.updateNotiz(notiz, verfuegungUri)
					.pipe(
						mergeMap(verfuegung => [
							updateVerfuegungNotizSuccessAction({ verfuegung }),
							loadVerfuegungenByVorgangAction(),
						])
					)
			)
		)
	);

	drucken = createEffect(() =>
		this.actions.pipe(
			ofType(createVerfuegungDruckAction),
			withLatestFrom(this.facade.verfuegung),
			switchMap(([, verfuegung]) =>
				this.service.drucken(verfuegung).pipe(map(file => createVerfuegungDruckSuccessAction({ file })))
			)
		)
	);

	download = createEffect(
		() =>
			this.actions.pipe(
				ofType(createVerfuegungDruckSuccessAction),
				withLatestFrom(this.verfahrenFacade.verfahrenSingle),
				tap(([{ file }, verfahren]) => {
					const aktenzeichen = verfahren.schnellzugriff.find(
						({ key }) => key === 'common.short_aktenzeichen'
					);
					const extension =
						file.type == 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
							? '.docx'
							: '.pdf';
					// response header "ContentDisposition" nicht mehr verfuegbar:
					const filename = aktenzeichen?.value?.replace(/ /g, '_') + '_verfuegung' + extension;
					saveAs(file, filename);
				})
			),
		{ dispatch: false }
	);
}
